import React from 'react';
import Tooltip, { RichExplanation } from './Tooltip.jsx';
import { lookupKPIGlossary } from './kpiGlossary.js';
import { lookupLineGlossary } from './statementGlossary.js';
import {
  CURRENT_PERIOD_HEADER_CLS, DEFAULT_PERIOD_HEADER_CLS,
  CURRENT_PERIOD_CELL_TINT, THEAD_BOTTOM_LINE, THEAD_CORNER_SHADOW, CELL_BORDER,
  STICKY_H_CELL, STICKY_H_CELL_LEFT, CURRENT_PERIOD_STICKY_BG,
  THEAD_VERT_PAD, STICKY_TREND_HEADER_CELL,
  STICKY_TREND_BODY_CELL, STICKY_TREND_SECTION_CELL, STICKY_TREND_BOX_GRID,
  STICKY_BOX_GRID,
} from './periodStyles.js';
import MiniSparkline, { SparklineDeltaBadge } from './MiniSparkline.jsx';
import CurrentPeriodProofBadge from './CurrentPeriodProofBadge.jsx';
import { RowSparkSegment, SparkDefs, makeSparkIds } from './RowSparkSegment.jsx';
import StatementFrame from './StatementFrame.jsx';
import { fmtMetric, isFlatSeries, seriesRange, computeTrendTone, seriesSlope, resolveTrendVisual } from './formatAmount.js';
import { useStickyCascade } from '../hooks/useStickyCascade.js';

const METRIC_LABEL_COL_WIDTH = 220;
const METRIC_TREND_COL_WIDTH = 100;

/* v8 Path L — MetricHorizonTable
   Generic "N metrics × M periods" data table, DRY cousin of
   FormalStatementTable. Periods go across, metrics go down, grouped
   by optional category heading rows. Current-period column is
   highlighted. Metric tone is colored per value. Per-metric format
   respects the same formatter keys the ratio engine emits (ratio,
   pct, days, months, x).

   Designed to be the renderer for any "financial metric over a time
   horizon" — ratios today, custom KPIs tomorrow. Visual language
   matches the 3-statement tables so users see everything as one
   consistent pattern: sticky left label column, top-aligned sticky
   header, gold-highlighted current period, hover tooltip with formula
   + explanation.

   Inputs:
     - title, subtitle
     - periods: string[]       — column labels (e.g. ['2026-03','2026-04',…])
     - currentPeriodLabel      — highlights the matching column gold
     - rows: [{
         ratio: 'Cash ratio',
         category: 'Liquidity',       // groups rows under a heading
         format: 'ratio'|'pct'|'days'|'months'|'x',
         tone: 'good'|'warn'|'bad'|'neutral',
         formula, hint, explanation,  // for tooltip
         values: number[],            // aligned with periods
       }]
*/

/* v8 Path U — fmtValue moved to shared formatAmount.js (fmtMetric). All
   amounts render in the normal text color (matches 3-statement filing
   style); tone still drives the Trend/delta glyph accents but NOT the
   value text, so the comparative table reads cleanly like a statement. */

/* Bucket the rows by category so headings render in order. */
function groupByCategory(rows) {
  const order = [];
  const map = {};
  for (const r of rows) {
    const cat = r.category || '';
    if (!(cat in map)) { map[cat] = []; order.push(cat); }
    map[cat].push(r);
  }
  return order.map(cat => ({ category: cat, lines: map[cat] }));
}

export default function MetricHorizonTable({
  title, subtitle, periods, currentPeriodLabel, currentPeriodProof = null,
  rows, entity, period,
  /* v8 Path Q — DRY sparkline column. When `showSparkline` is true, an
     extra column rides between the label and the periods showing a
     MiniSparkline of the row's full value series (the "Trend" quick
     glance). `gradient` toggles the area-fill under the Trend line. */
  showSparkline = true,
  gradient = false,
  /* v8 Path T — in-cell row-sparkline underlay. Each value cell gets an
     absolutely-positioned segment that connects its period's value to
     the previous period's value (same pattern as FormalStatementTable,
     now DRY via RowSparkSegment.jsx). `showRowSparkline` toggles this
     behind-the-number trend; `gradient` reuses the same area-fill prop
     so one ScenarioBar toggle lights up every table at once. */
  showRowSparkline = true,
  sparkIdPrefix = 'mh',
  /* v8 Path AI — when true, sparkline colors reflect metric polarity
     (`line.betterWhen`) rather than raw direction. Off by default so
     consumers that don't opt in keep the legacy "rising=green" look. */
  semanticTone = false,
}) {
  const groups = groupByCategory(rows || []);
  const currentIdx = currentPeriodLabel
    ? periods.findIndex(p => p === currentPeriodLabel)
    : -1;
  const isCurrent = (i) => i === currentIdx;
  const sparkColWidth = showSparkline ? METRIC_TREND_COL_WIDTH : 0;
  const tableMinW = METRIC_LABEL_COL_WIDTH + sparkColWidth + periods.length * 110;
  const tableStyle = {
    minWidth: tableMinW,
    '--lens-label-col-width': `${METRIC_LABEL_COL_WIDTH}px`,
    '--lens-trend-col-width': `${METRIC_TREND_COL_WIDTH}px`,
  };
  const { upId, downId, neutralId } = makeSparkIds(sparkIdPrefix);
  const scrollRef = React.useRef(null);
  useStickyCascade(scrollRef, 24);
  const hasRows = groups.some(g => g.lines.length > 0);
  const trendStickyBase = `${STICKY_TREND_BODY_CELL} ${STICKY_TREND_BOX_GRID}`;
  const trendStickySection = `${STICKY_TREND_SECTION_CELL} ${STICKY_TREND_BOX_GRID}`;
  const labelStickyBox = STICKY_BOX_GRID;

  /* Compute [min,max,flat,rising] for a single row. DRY with FormalStatementTable:
     - `rising` is ROW-LEVEL (end-of-series vs start-of-series), NOT per-cell.
       This means every in-cell RowSparkSegment in a row shares the same
       green/red, and the Trend column's MiniSparkline uses the same tone.
       Matches the filing-style visual in Formal Statements: if a row is
       trending up overall, every segment is green; trending down, every
       segment is red. No more alternating per-cell color. */
  const rangeCache = new WeakMap();
  const rowTrend = (line) => {
    if (!line || !Array.isArray(line.values)) {
      return { min: 0, max: 0, flat: true, rising: true, tone: 'good', series: [] };
    }
    const cached = rangeCache.get(line);
    if (cached) return cached;
    /* v8 Path AK — magnitude-view resolution. For always-negative
       currency rows (rare in MetricHorizonTable since most KPIs are
       revenues/equity/ratios, but possible for things like "Base burn"
       if ever expressed as negative), the helper flips to abs + infers
       betterWhen='lower'. Non-currency ratios (pct/days/months/x) and
       mixed-sign series fall through unchanged. */
    const trend = resolveTrendVisual(line.values, line.format, line.betterWhen, semanticTone);
    const { series, rising, tone, flat } = trend;
    const nums = series.filter(v => typeof v === 'number' && Number.isFinite(v));
    const min = nums.length ? Math.min(...nums) : 0;
    const max = nums.length ? Math.max(...nums) : 0;
    const out = { min, max, flat, rising, tone, series };
    rangeCache.set(line, out);
    return out;
  };

  /* Styling mirrors FormalStatementTable — same font, same sticky
     patterns, same current-period accent.
     v8 Path AG — outer wrapper + scroll container + StatementHeader
     now live in <StatementFrame/>. Ref forwards to the scroll div
     so useStickyCascade still targets the correct sticky scope. */
  return (
    <StatementFrame
      ref={scrollRef}
      entity={entity}
      title={title}
      subtitle={subtitle}
      period={period}
      defs={showRowSparkline && <SparkDefs upId={upId} downId={downId} neutralId={neutralId} />}
      pinnedChrome={<CurrentPeriodProofBadge label={currentPeriodLabel} found={currentIdx >= 0} proof={currentPeriodProof} />}
    >
      <table
          className="border-collapse text-[13px] table-fixed w-full"
          style={tableStyle}
        >
          <colgroup>
            <col style={{ width: 'var(--lens-label-col-width)' }} />
            {showSparkline && <col style={{ width: 'var(--lens-trend-col-width)' }} />}
            {periods.map((_, i) => <col key={i} style={{ minWidth: '110px' }} />)}
          </colgroup>

          {/* v8 Path X — thead bottom line via inset shadow (DRY via
              THEAD_BOTTOM_LINE / THEAD_CORNER_SHADOW constants). The
              line paints INSIDE each cell, so it pins with the sticky
              thead and never collides with tbody's first-row top. */}
          <thead>
            <tr>
              <th className={`sticky left-0 top-0 z-[30] bg-[#1c1c30] text-left text-[0.72rem] font-semibold text-text-dim px-[0.6rem] ${THEAD_VERT_PAD} whitespace-nowrap font-mono ${THEAD_CORNER_SHADOW}`}>
                Metric
              </th>
              {showSparkline && (
                <th
                  className={`${STICKY_TREND_HEADER_CELL} text-center text-[0.72rem] font-semibold text-text-dim px-[0.4rem] ${THEAD_VERT_PAD} whitespace-nowrap font-mono ${STICKY_TREND_BOX_GRID}`}
                >
                  Trend
                </th>
              )}
              {periods.map((p, i) => (
                <th
                  key={i}
                  className={`sticky top-0 z-[20] text-right text-[0.72rem] font-semibold px-[0.4rem] ${THEAD_VERT_PAD} whitespace-nowrap font-mono min-w-[110px] ${THEAD_BOTTOM_LINE} ${isCurrent(i) ? CURRENT_PERIOD_HEADER_CLS : DEFAULT_PERIOD_HEADER_CLS}`}
                  title={isCurrent(i) ? 'Current period' : undefined}
                >
                  {isCurrent(i) ? `● ${p}` : p}
                </th>
              ))}
            </tr>
          </thead>

          {/* v8 Path Z — one <tbody> per category with `position: relative`.
              Per CSS, a sticky descendant is bounded by its nearest POSITIONED
              (non-static) ancestor. Adding position:relative on each tbody
              scopes the H1 sticky rectangle to THAT section: when the user
              scrolls past the whole section, the tbody's top edge travels
              above the viewport, and sticky un-pins H1 — handing the top
              slot to the next section's H1 (genuine push-upward cascade). */}
          {hasRows ? groups.map((g, gi) => (
            <tbody
              key={gi}
              /* v8 Path AC.2 — `[&>tr:last-child>td]:!pb-0` kills the
                 padding-bottom on each section's last data row, so the
                 NEXT section's H1 banner sits flush against it (no
                 bleeding strip between sections). DRY with the H2
                 equivalent in FormalStatementTable. */
              className={`[&>tr:last-child>td]:!pb-0 ${gi === 0 ? '[&>tr:first-child>td]:border-t-0 [&>tr:first-child>*]:border-t-0' : ''}`}
            >
              <React.Fragment>
                {g.category && (
                  /* v8 Path AC — multi-cell H1 row. Label cell sticky-left
                      keeps category name visible during horizontal scroll;
                      other cells are solid-bg spacers covering any overflow. */
                  <tr data-sticky-row="h1">
                    <td className={`${STICKY_H_CELL_LEFT} bg-[#1c1c30] whitespace-nowrap px-2 py-0 font-bold text-[13px] text-accent-gold uppercase tracking-[0.05em] overflow-hidden text-ellipsis ${labelStickyBox}`} title={g.category}>
                      {g.category}
                    </td>
                    {showSparkline && (
                      <td className={`${trendStickySection} py-0`} />
                    )}
                    {periods.map((_, i) => (
                      <td
                        key={i}
                        className={`${STICKY_H_CELL} py-0 ${isCurrent(i) ? CURRENT_PERIOD_STICKY_BG : 'bg-[#1c1c30]'}`}
                      />
                    ))}
                  </tr>
                )}
                {g.lines.map((line, li) => {
                  /* Row-level trend — hoisted out of the per-cell map so
                     every segment + the Trend-column MiniSparkline use
                     the SAME color (matches Formal Statements).
                     v8 Path AI — `tone` is pre-computed by rowTrend
                     using semantic polarity (betterWhen) when the
                     scenario toggle is on; falls back to legacy
                     rising=good otherwise. */
                  const { min, max, flat, rising, tone: trendTone, series: plotSeries } = rowTrend(line);
                  return (
                    <tr
                      key={li}
                      className="hover:[&>td]:bg-white/[0.03]"
                    >
                      <td className={`sticky left-0 z-[10] !bg-[#1c1c30] min-w-[200px] max-w-[260px] border border-white/[0.06] px-[0.6rem] py-[0.4rem] ${labelStickyBox}`}>
                        <Tooltip
                          placement="right"
                          delay={120}
                          maxWidth={360}
                          content={(() => {
                            /* When the line object doesn't carry rich
                               metadata (KPI timeline rows don't — only
                               ratios do), fall back to the KPI glossary
                               first, then the statement-line glossary.
                               Line-level fields always win when present. */
                            const g = (!line.formula && !line.hint && !line.explanation)
                              ? (lookupKPIGlossary(line.ratio) ?? lookupLineGlossary(line.ratio))
                              : null;
                            return (
                              <RichExplanation
                                title={line.ratio}
                                formula={line.formula ?? g?.formula}
                                hint={line.hint ?? g?.hint}
                                explanation={line.explanation ?? g?.explanation}
                                direction={line.direction ?? g?.direction}
                                betterWhen={line.betterWhen ?? g?.betterWhen}
                              />
                            );
                          })()}
                        >
                          <span className="block cursor-help">
                            <span className="text-[0.82rem] text-text block truncate">{line.ratio}</span>
                            {line.formula && (
                              <em className="text-[0.66rem] text-text-dim not-italic block truncate">{line.formula}</em>
                            )}
                          </span>
                        </Tooltip>
                      </td>
                      {showSparkline && (
                        <td
                          className={`${trendStickyBase} hover:!bg-[#1e1e3a] px-[0.3rem] py-[0.4rem] text-center align-middle whitespace-nowrap`}
                        >
                          {!flat ? (
                            <span className="inline-flex items-center gap-[4px]">
                              <MiniSparkline
                                series={plotSeries}
                                tone={trendTone}
                                size="sm"
                                gradient={gradient}
                                highlightIdx={currentIdx >= 0 ? currentIdx : undefined}
                                ariaLabel={`${line.ratio} trend`}
                              />
                              <SparklineDeltaBadge series={plotSeries} format={line.format} />
                            </span>
                          ) : (
                            <span className="text-text-dim text-[0.7rem]" title="No movement across horizon">—</span>
                          )}
                        </td>
                      )}
                      {line.values.map((v, pi) => {
                        /* v8 Path AK — sparkline segment uses plotSeries
                           (magnitude-view when applicable). Cell still
                           displays raw `v` via fmtMetric so the number
                           rendering stays faithful to stored sign. */
                        const plotPrev = pi > 0 ? plotSeries[pi - 1] : null;
                        const plotCurr = plotSeries[pi];
                        return (
                          <td
                            key={pi}
                            className={`relative border border-white/[0.06] px-[0.5rem] py-[0.4rem] text-right tabular-nums text-text ${isCurrent(pi) ? CURRENT_PERIOD_CELL_TINT : ''}`}
                          >
                            {showRowSparkline && !flat && pi > 0 && (
                              <RowSparkSegment
                                prev={plotPrev}
                                curr={plotCurr}
                                min={min}
                                max={max}
                                /* v8 Path AI — pass `tone` so the segment color
                                   reflects favorability (betterWhen polarity)
                                   rather than raw direction. `rising` still
                                   carried for legacy consumers that want the
                                   raw direction arrow. */
                                tone={trendTone}
                                rising={rising}
                                gradient={gradient}
                                gradUpId={upId}
                                gradDownId={downId}
                                gradNeutralId={neutralId}
                              />
                            )}
                            <span className="relative z-[2] [text-shadow:0_1px_3px_rgba(0,0,0,0.85),0_0_1px_rgba(0,0,0,0.8)]">{fmtMetric(v, line.format, 0)}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            </tbody>
          )) : (
            <tbody>
              <tr className="hover:[&>td]:bg-white/[0.03]">
                <td className={`sticky left-0 z-[10] !bg-[#1c1c30] min-w-[200px] max-w-[260px] border ${CELL_BORDER} px-[0.6rem] py-[0.4rem] ${labelStickyBox}`}>
                  <span className="block text-[0.82rem] text-text-dim truncate">No metrics</span>
                </td>
                {showSparkline && (
                  <td
                    className={`${trendStickyBase} hover:!bg-[#1e1e3a] px-[0.3rem] py-[0.4rem] text-center align-middle whitespace-nowrap`}
                  >
                    <span className="text-text-dim text-[0.7rem]" title="No movement across horizon">—</span>
                  </td>
                )}
                {periods.map((_, i) => (
                  <td
                    key={i}
                    className={`relative border ${CELL_BORDER} px-[0.5rem] py-[0.4rem] text-right tabular-nums text-text ${isCurrent(i) ? CURRENT_PERIOD_CELL_TINT : ''}`}
                  >
                    <span className="relative z-[2] text-text-dim">—</span>
                  </td>
                ))}
              </tr>
            </tbody>
          )}
        </table>
    </StatementFrame>
  );
}

/* Memoized — same logic as FormalStatementTable: dense rows × many
   columns → skip re-renders when inputs haven't changed. */
const MetricHorizonTableMemo = React.memo(MetricHorizonTable);
export { MetricHorizonTableMemo };
